import { useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useFundingStore } from '../../store/useFundingStore';

interface Props {
  onClose: () => void;
}

export default function CreateFundingModal({ onClose }: Props) {
  const { createOpportunity } = useFundingStore();
  const [form, setForm] = useState({
    name: '',
    description: '',
    funderName: '',
    funderType: 'other',
    fundingType: 'grant',
    amount: '',
    stage: 'prospect',
    owner: '',
    priority: 'medium',
    deadlineDate: '',
    category: '',
    programArea: '',
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error('Opportunity name is required');
      return;
    }
    if (!form.funderName.trim()) {
      toast.error('Funder name is required');
      return;
    }

    setLoading(true);
    try {
      await createOpportunity({
        ...form,
        amount: form.amount ? parseFloat(form.amount) : 0,
      } as any);
      toast.success('Funding opportunity created');
      onClose();
    } catch (err: any) {
      toast.error(err.message || 'Failed to create opportunity');
    } finally {
      setLoading(false);
    }
  };

  const update = (field: string, value: string) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  return (
    <Transition appear show={true} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-lg bg-white dark:bg-gray-800 rounded-xl shadow-xl p-6 max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between mb-6">
                  <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    New Funding Opportunity
                  </Dialog.Title>
                  <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                    <X size={18} className="text-gray-500 dark:text-gray-400" />
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Opportunity Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Opportunity Name *
                    </label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      placeholder="e.g., HHS Innovation Grant 2026"
                      autoFocus
                    />
                  </div>

                  {/* Funder Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Funder / Organization *
                    </label>
                    <input
                      type="text"
                      value={form.funderName}
                      onChange={(e) => update('funderName', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      placeholder="e.g., National Science Foundation"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <textarea
                      value={form.description}
                      onChange={(e) => update('description', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      rows={2}
                      placeholder="Brief description of the funding opportunity..."
                    />
                  </div>

                  {/* Funder Type + Funding Type */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Funder Type</label>
                      <select
                        value={form.funderType}
                        onChange={(e) => update('funderType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="federal">Federal</option>
                        <option value="state">State</option>
                        <option value="local">Local</option>
                        <option value="foundation">Foundation</option>
                        <option value="corporate">Corporate</option>
                        <option value="nonprofit">Nonprofit</option>
                        <option value="international">International</option>
                        <option value="other">Other</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Funding Type</label>
                      <select
                        value={form.fundingType}
                        onChange={(e) => update('fundingType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="grant">Grant</option>
                        <option value="contract">Contract</option>
                        <option value="cooperative-agreement">Cooperative Agreement</option>
                        <option value="subgrant">Subgrant</option>
                        <option value="sponsorship">Sponsorship</option>
                        <option value="donation">Donation</option>
                        <option value="loan">Loan</option>
                        <option value="venture">Venture Capital</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>

                  {/* Amount + Priority */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Amount ($)</label>
                      <input
                        type="number"
                        value={form.amount}
                        onChange={(e) => update('amount', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                        placeholder="0"
                        min="0"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
                      <select
                        value={form.priority}
                        onChange={(e) => update('priority', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                  </div>

                  {/* Stage + Owner */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pipeline Stage</label>
                      <select
                        value={form.stage}
                        onChange={(e) => update('stage', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      >
                        <option value="prospect">Prospect</option>
                        <option value="qualifying">Qualifying</option>
                        <option value="preparing">Preparing</option>
                        <option value="submitted">Submitted</option>
                        <option value="under-review">Under Review</option>
                        <option value="awarded">Awarded</option>
                        <option value="declined">Declined</option>
                        <option value="withdrawn">Withdrawn</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Owner</label>
                      <input
                        type="text"
                        value={form.owner}
                        onChange={(e) => update('owner', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                        placeholder="Assigned owner"
                      />
                    </div>
                  </div>

                  {/* Deadline + Category */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Deadline</label>
                      <input
                        type="date"
                        value={form.deadlineDate}
                        onChange={(e) => update('deadlineDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                      <input
                        type="text"
                        value={form.category}
                        onChange={(e) => update('category', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                        placeholder="e.g., Research, Infrastructure"
                      />
                    </div>
                  </div>

                  {/* Program Area */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Program Area</label>
                    <input
                      type="text"
                      value={form.programArea}
                      onChange={(e) => update('programArea', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                      placeholder="Which department or program area"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={loading}
                      className="px-4 py-2 text-sm bg-accent text-white rounded-lg hover:bg-accent/90 disabled:opacity-50 transition-colors"
                    >
                      {loading ? 'Creating...' : 'Create Opportunity'}
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
